import { Project } from "@app/projects";
import getOptions from "@app/projects/create/data/options";
import { TooltipWrapper } from "@app/shared";
import { QESCheckbox } from "@app/shared/qes-standards";
import { UserContext } from "@app/user";
import { uniqueId } from "lodash";
import { Component, Fragment, RefObject } from "react";
import BulkProjectEditChange from "../edit/bulkProjectEditChange";
import { ProjectExclusionType } from "../enumerators";

interface EditDisplayOptionsProps {
  sidePanelId?: string;
  accuracyAndAdequacy: boolean;
  checkboxClass: string;
  disabled: boolean;
  disableUnsupportedOptions: boolean;
  onCheckBoxChange: (property: UpdateResourceKeys) => void;
  onImpactExclusionsChange: (exclusion: ProjectExclusionType) => void;
  onNetworkExclusionsChange: (exclusion: ProjectExclusionType) => void;
  project: Project;
  shared: boolean;
  hideClosestProviderReportOption: boolean;
  hideCMSApplicationCredit: boolean;
  hideCareJourneyScores: boolean;
  hideCreateStandards: boolean;
  hideMotiveAppropriatenessScore: boolean;
  hideEnterpriseComplianceDashboard: boolean;
  hidePracAtLocCondifence?: boolean;
  hideProviderClaimsActivity?: boolean;
  hideStrictTimeDistance: boolean;
  ecdCheckboxRef?: RefObject<HTMLInputElement>;
  isZipProject: boolean;
  showNetworkIntegrityDashboard: boolean;

  // Be careful with the following properties:
  // They look unused, but they aren't (see: this.props[field.key])
  // Names must match those in 'ts\projects\create\data\options.ts'
  excludeDeactivatedFacilitiesImpact?: boolean;
  excludeDeactivatedFacilitiesNetwork?: boolean;
  excludeDeactivatedProvidersImpact?: boolean;
  excludeDeactivatedProvidersNetwork?: boolean;
  excludeInvalidNppesNpiFacilitiesImpact?: boolean;
  excludeInvalidNppesNpiFacilitiesNetwork?: boolean;
  excludeInvalidNppesNpiProvidersImpact?: boolean;
  excludeInvalidNppesNpiProvidersNetwork?: boolean;
  excludeMedicareOptOutProvidersImpact?: boolean;
  excludeMedicareOptOutProvidersNetwork?: boolean;
  excludeMissingNpiFacilitiesImpact?: boolean;
  excludeMissingNpiFacilitiesNetwork?: boolean;
  excludeMissingNpiProvidersImpact?: boolean;
  excludeMissingNpiProvidersNetwork?: boolean;
  excludeOIGExcludedProvidersImpact?: boolean;
  excludeOIGExcludedProvidersNetwork?: boolean;
  excludePrecludedProvidersImpact?: boolean;
  excludePrecludedProvidersNetwork?: boolean;
  useAdequacy: boolean;
  useAccuracy: boolean;
  showAffiliationsInsights: boolean;
  displayOnNetworkAccordion: boolean;
}

type UpdateResourceKeys = keyof Project | keyof BulkProjectEditChange;

export class EditDisplayOptions extends Component<EditDisplayOptionsProps> {
  public render() {
    return (
      <Fragment>
        <div className="tab-contents col-12">
          {!this.props.shared &&
            <>
              <UserContext.Consumer>
                {(user) => (
                  getOptions<Project>({
                    accuracyAndAdequacy: this.props.accuracyAndAdequacy,
                    checkboxRefs: {
                      "includeInEnterpriseComplianceDashboard": this.props.ecdCheckboxRef,
                    },
                    disableUnsupportedOptions: this.props.disableUnsupportedOptions,
                    displayOnNetworkAccordion: this.props.displayOnNetworkAccordion,
                    enablePatternOfCareProvidersOption: this.props.project.hasZipSummaries && this.props.project.showNextClosestNetworkProviders,
                    enableRegulatoryExceptionReportsOption: this.props.project.hasZipSummaries && this.props.project.showNextClosestNetworkProviders,
                    hideCMSApplicationCredit: this.props.hideCMSApplicationCredit,
                    hideCareJourneyScores: this.props.hideCareJourneyScores,
                    hideClosestProviderReport: this.props.hideClosestProviderReportOption,
                    hideCreateStandards: this.props.hideCreateStandards,
                    hideEnterpriseComplianceDashboard: this.props.hideEnterpriseComplianceDashboard,
                    hideMotiveAppropriatenessScore: this.props.hideMotiveAppropriatenessScore,
                    hidePciData: !this.props.useAdequacy,
                    hidePracAtLocCondifence: this.props.hidePracAtLocCondifence,
                    hideProviderClaimsActivity: this.props.hideProviderClaimsActivity,
                    hideStrictTimeDistance: this.props.hideStrictTimeDistance,
                    isZipProject: this.props.isZipProject,
                    showAffiliationsInsights: this.props.showAffiliationsInsights, 
                    showNetworkIntegrityDashboard: this.props.showNetworkIntegrityDashboard,
                    showPciDataChecked: this.props.project.showPciData,
                    useAccuracy: this.props.useAccuracy,
                    userRoles: user.roles,
                  }).map((fieldset) => (
                    fieldset.fields.length > 0 && (
                      <div className="form-group row" key={fieldset.title}>
                        <label className="col-12 col-sm-3 col-lg-3 col-form-label pt-0">
                          {fieldset.title}
                        </label>
                        <div className="col-12 col-sm-9 col-lg-8">
                          {fieldset.fields.map((field) => (
                            !!field.exclusionType ? (
                              this.renderExclusionCheckbox(field.title,
                                field.exclusionType,
                                this.props.checkboxClass,
                                this.props.disabled || field.disabled,
                                !!field.impact,
                                this.props[field.key as keyof EditDisplayOptionsProps] === undefined ? undefined : !!this.props[field.key as keyof EditDisplayOptionsProps],
                                field.ref,
                              )
                            ) :
                              (
                                this.renderCheckbox(
                                  field.key,
                                  field.title,
                                  field.key as keyof Project,
                                  this.props.checkboxClass,
                                  "col-12 col-sm-9 col-lg-8",
                                  this.props.disabled || field.disabled,
                                  this.props.project[field.key as keyof Project] === undefined ? undefined : !!this.props.project[field.key as keyof Project],
                                  field.tooltip,
                                  !!field.dependency,
                                  field.ref
                                )
                              )
                          ))}
                        </div>
                      </div>
                    )
                  ))
                )}
              </UserContext.Consumer>
            </>
          }
        </div>
      </Fragment>
    );
  }
  private renderExclusionCheckbox(
    checkboxText: string,
    projectExclusionType: ProjectExclusionType,
    checkboxCssClass: string,
    disabled: boolean,
    impact: boolean,
    checkboxValue?: boolean,
    checkboxRef?: RefObject<HTMLInputElement>,
  ) {
    if (this.props.shared && !checkboxValue) {
      //only display checked checkboxes for shared users
      return null;
    }
    const inputId = uniqueId();
    return (
      <div className="col-12" key={checkboxText}>
        <QESCheckbox
          containerClassName={`${checkboxCssClass} form-check mt-0`}
          label={<span style={{ color: checkboxValue == undefined ? "red" : "inherit" }}>{checkboxText}</span>}
          id={inputId}
          className="custom-control-inline"
          checked={checkboxValue}
          onChange={() => { impact ? this.props.onImpactExclusionsChange(projectExclusionType) : this.props.onNetworkExclusionsChange(projectExclusionType); }}
          disabled={disabled}
          testId={"edit-display-options__" + checkboxText + "-checkbox"}
          indeterminate={checkboxValue === undefined}
          checkboxRef={checkboxRef}
        />
      </div>
    );
  }

  private renderCheckbox(
    checkboxKey: keyof Project,
    checkboxText: string,
    eventProperty: UpdateResourceKeys,
    checkboxCssClass: string,
    outerDivCssClass: string,
    disabled: boolean,
    checkboxValue?: boolean,
    tipText?: string,
    isDependent?: boolean,
    checkboxRef?: RefObject<HTMLInputElement>,
  ) {
    if (this.props.shared && !checkboxValue) {
      //only display checked checkboxes for shared users
      return null;
    }
    const inputId = uniqueId();
    return (
      <div className={outerDivCssClass} key={checkboxText}>
        <TooltipWrapper text={tipText} containerId={this.props.sidePanelId}>
          <QESCheckbox
            containerClassName={`${checkboxCssClass} form-check mt-0${isDependent ? " ml-4" : ""}`}
            label={
              <span style={{ color: checkboxValue == undefined ? "red" : "inherit" }}>{checkboxText}</span>
            }
            id={inputId}
            className="custom-control-inline"
            checked={checkboxValue}
            onChange={() => { this.props.onCheckBoxChange(eventProperty); }}
            disabled={disabled}
            testId={"edit-display-options__" + checkboxKey + "-checkbox"}
            indeterminate={checkboxValue === undefined}
            checkboxRef={checkboxRef}
          />
        </TooltipWrapper>
      </div>
    );
  }
}
